import { QuantitySelectorComponent } from '@theme/component-quantity-selector';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A tap on the remove state that lands this soon after the button turned into
 * it is treated as the tail of a fast double tap on minus, not a decision.
 */
const REMOVE_ARM_DELAY_MS = 450;

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * There is no separate remove button on cart lines: at the minimum quantity the
 * minus button becomes the remove button (`data-removes`, trash icon). Removing
 * takes a deliberate tap on a control that visibly changed, instead of a small
 * trash icon sitting next to the stepper where it is easy to hit by mistake.
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /** @type {number} When the minus button last switched into the remove state. */
  #removeArmedAt = 0;

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Whether minus removes the line right now: at the minimum quantity, or
   * always when the line's quantity is locked (`data-remove-only`). Liquid
   * renders `data-no-remove` when the line's instructions forbid removal.
   * Sold-out lines keep a disabled input but must stay removable, so the
   * input's disabled state is deliberately not consulted.
   * @param {number} value
   * @param {number} min
   * @returns {boolean}
   */
  #minusRemoves(value, min) {
    if (this.hasAttribute('data-no-remove')) return false;
    return this.hasAttribute('data-remove-only') || value <= min;
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const removes = this.#minusRemoves(value, min);

    if (removes && !this.hasAttribute('data-removes')) this.#removeArmedAt = performance.now();
    this.toggleAttribute('data-removes', removes);

    minusButton.disabled = value <= min && !removes;
    plusButton.disabled =
      this.hasAttribute('data-remove-only') || (effectiveMax !== null && value >= effectiveMax);
  }

  /**
   * Minus at the minimum asks the cart to drop the line. It goes through the
   * same debounced quantity event as every other change, so a still-pending
   * change on this line is superseded instead of landing after the removal
   * (line numbers shift once a line is gone).
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const { min, value } = this.getCurrentValues();
    if (!this.#minusRemoves(value, min)) {
      super.decreaseQuantity(event);
      return;
    }

    event.preventDefault();
    if (performance.now() - this.#removeArmedAt < REMOVE_ARM_DELAY_MS) return;

    const { quantityInput } = this.refs;
    this.refs.minusButton.disabled = true;
    this.dispatchEvent(new QuantitySelectorUpdateEvent(0, Number(quantityInput.dataset.cartLine) || undefined));
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
